import { FC, FormEvent, useState } from "react";

import {
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { postPartnerOrgBootstrap } from "@rubi/api-generated";
import { useNavigate } from "react-router-dom";

import { Breadcrumbs, Crumb, DevPage, ErrorMsg, Section } from "./common";

export const OnboardPartnerWizard: FC = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [shortTitle, setShortTitle] = useState("");
  const [grantAddSupplemental, setGrantAddSupplemental] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (busy) return;
    if (title.trim().length === 0) {
      setError("Title cannot be blank");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const response = await postPartnerOrgBootstrap({
        body: {
          title: title.trim(),
          short_title: shortTitle.trim() || null,
          stytch_slug: null,
          grant_add_supplemental: grantAddSupplemental,
        },
        throwOnError: false,
      });
      if (response.status === 200) {
        navigate(`/dev_admin/partner_org/${response.data!.partner_org.id}`, {
          state: {
            partnerOrg: response.data!.partner_org,
            hostingOrg: response.data!.org,
          },
        });
      } else {
        setError("" + response.status + " " + response.error?.console_msg);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create partner");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DevPage
      title="Onboard partner"
      breadcrumbs={
        <Breadcrumbs>
          <Crumb>
            <a href="/dev_admin">Dev admin</a>
          </Crumb>
          <Crumb>
            <a href="/dev_admin/partner_org">Partner Orgs</a>
          </Crumb>
          <Crumb>Onboard partner</Crumb>
        </Breadcrumbs>
      }
    >
      <Section>
        <Stack
          component="form"
          onSubmit={handleCreate}
          spacing={2}
          sx={{ maxWidth: 560 }}
        >
          <Typography variant="body2" color="text.secondary">
            Creates the Stytch organization, the partner_org row, and the
            rubi_org that hosts the partner&apos;s staff logins — pre-approved
            for the RubiPARTNER app with the permissions below as the default
            for new staff.
          </Typography>
          <ErrorMsg msg={error} />
          <TextField
            label="Title"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            helperText='Shown in the "share with partner" picker and as the partner&apos;s org name.'
          />
          <TextField
            label="Short title"
            value={shortTitle}
            onChange={(e) => setShortTitle(e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={grantAddSupplemental}
                onChange={(e) => setGrantAddSupplemental(e.target.checked)}
              />
            }
            label="Allow partner staff to add supplemental reports (in addition to viewing them)"
          />
          <Button type="submit" variant="contained" disabled={busy}>
            {busy ? "Creating…" : "Create partner"}
          </Button>
        </Stack>
      </Section>
    </DevPage>
  );
};
